import { useReducer, useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import announcementFormReducer from 'containers/pages/announcementManagement/reducers/announcementFormReducer';
import { initAnnouncement, initAnnouncementValid, loadedAnnouncementValid } from 'containers/pages/announcementManagement/data';
import { handleAnnouncement, makeAnnouncementObj, convertAnnouncement } from 'containers/pages/announcementManagement/libs';
import { createAnnouncement, updateAnnouncement } from 'api/announcement';
import * as TY from 'containers/pages/announcementManagement/constants';
import { useNotificationProvider } from 'hooks';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import { AnnouncementFormData, AnnouncementValidation, Announcement } from 'containers/pages/announcementManagement/types';

interface UseAnnouncementFormUnifiedProps {
  mode: 'add' | 'edit';
  announcement?: Announcement | null;
  onSaveSuccess?: () => void;
  onDelete?: (id: number) => Promise<void>;
}

function useAnnouncementFormUnified({ mode, announcement, onSaveSuccess, onDelete }: UseAnnouncementFormUnifiedProps) {
  const { notifySuccess, notifyError } = useNotificationProvider();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { userInfo } = useSelector(selectUserInfo) as any;
  const createdByUserName = `${userInfo.firstName} ${userInfo.lastName}`.trim() || userInfo.username;
  const [formState, dispatch] = useReducer(announcementFormReducer, { ...initAnnouncement });
  const [valid, setValid] = useState<AnnouncementValidation>({ ...initAnnouncementValid });
  const [isLoading, setIsLoading] = useState(false);

  // Load announcement data when editing
  useEffect(() => {
    if (mode === 'edit' && announcement) {
      const converted = convertAnnouncement(announcement);
      dispatch({ type: TY.ANNOUNCEMENT_CONVERT, payload: converted });
      setValid({ ...loadedAnnouncementValid });
    }
  }, [mode, announcement]);

  // Central field change handler
  const onForm = useCallback(
    (e: { target: { name: string; value: string } }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      handleAnnouncement(e, dispatch, setValid as any);
    },
    [],
  );

  // Date change handler
  const onDateChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any) => {
      const dateStr = value ? value.format('YYYY-MM-DD') : null;
      dispatch({ type: TY.EXPIRE_DATE, payload: dateStr });
      setValid((prev) => ({
        ...prev,
        ExpireDate: {
          isErr: !dateStr,
          errMsg: !dateStr ? 'Expire date is required' : '',
          canNotSave: !dateStr,
        },
      }));
    },
    [],
  );

  // Compute canNotSave
  const canNotSave = Object.values(valid).some((v) => v.canNotSave);

  // Save handler
  const onSave = useCallback(async () => {
    const currentState = formState as AnnouncementFormData;
    let hasErrors = false;
    const newValid = { ...valid };

    if (!currentState.Title?.trim()) {
      newValid.Title = { isErr: true, errMsg: 'Title is required', canNotSave: true };
      hasErrors = true;
    } else if (currentState.Title.trim().length > 150) {
      newValid.Title = { isErr: true, errMsg: 'Title must be 150 characters or less', canNotSave: true };
      hasErrors = true;
    }

    if (!currentState.Description?.trim()) {
      newValid.Description = { isErr: true, errMsg: 'Description is required', canNotSave: true };
      hasErrors = true;
    } else if (currentState.Description.trim().length > 2000) {
      newValid.Description = { isErr: true, errMsg: 'Description must be 2000 characters or less', canNotSave: true };
      hasErrors = true;
    }

    if (!currentState.ExpireDate) {
      newValid.ExpireDate = { isErr: true, errMsg: 'Expire date is required', canNotSave: true };
      hasErrors = true;
    }

    if (hasErrors) {
      setValid(newValid);
      return;
    }

    setIsLoading(true);
    try {
      const payload = makeAnnouncementObj(currentState, createdByUserName);
      if (mode === 'edit' && currentState.ItemID) {
        await updateAnnouncement(currentState.ItemID, payload);
        notifySuccess('Announcement updated successfully');
      } else {
        await createAnnouncement(payload);
        notifySuccess('Announcement created successfully');
      }
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      console.error('[useAnnouncementFormUnified] Save failed:', error);
      notifyError('Failed to save announcement. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [formState, valid, mode, onSaveSuccess, notifySuccess, notifyError, createdByUserName]);

  // Reset handler
  const onReset = useCallback(() => {
    if (mode === 'add') {
      dispatch({ type: TY.RESET, payload: { ...initAnnouncement } });
      setValid({ ...initAnnouncementValid });
    }
    if (mode === 'edit' && announcement) {
      const converted = convertAnnouncement(announcement);
      dispatch({ type: TY.ANNOUNCEMENT_CONVERT, payload: converted });
      setValid({ ...loadedAnnouncementValid });
    }
  }, [mode, announcement]);

  const state = {
    announcement: formState,
    valid,
    isLoading,
    canNotSave,
  };

  const handler = {
    onForm,
    onDateChange,
    onSave,
    onReset,
  };

  return { state, handler };
}

export default useAnnouncementFormUnified;
